import React, { useState, useEffect } from 'react';
import s from './s.module.less';
import OnboardingStepContainer from '../OnboardingStepContainer';
import CheckboxOption from '../CheckboxOption';
import ActionButtons from '../ActionButtons';
import useSettings from 'hooks/useSettings';
import { EnumFields } from 'types/enumerationData';
import { sortByProperty } from 'utils/form';
import cx from 'classnames';
import { Input, Select } from 'antd';
// import SearchIcon from 'assets/common/search.svg';

interface LicenseSelectionStepProps {
    onLicensesChange?: (licenses: string[]) => void;
    onBack?: () => void;
    onNext?: () => void;
    currentStep?: number;
    totalSteps?: number;
    initialLicenses?: string[];
}

export default function LicenseSelectionStep({
    onLicensesChange,
    onBack,
    onNext,
    currentStep = 3,
    totalSteps = 7,
    initialLicenses = [],
}: LicenseSelectionStepProps) {
    // eslint-disable-next-line no-console
    console.log('LicenseSelectionStep rendered with props:', {
        currentStep,
        totalSteps,
        initialLicenses,
        initialLicensesLength: initialLicenses.length,
    });
    const [selectedLicenses, setSelectedLicenses] = useState<string[]>(initialLicenses);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
    const [setting, loading] = useSettings();

    // 同步 initialLicenses 到 selectedLicenses 状态
    useEffect(() => {
        // eslint-disable-next-line no-console
        console.log('useEffect triggered - initialLicenses:', initialLicenses, 'selectedLicenses:', selectedLicenses);
        if (initialLicenses.length > 0 && selectedLicenses.length === 0) {
            // eslint-disable-next-line no-console
            console.log('Syncing initialLicenses to selectedLicenses');
            setSelectedLicenses(initialLicenses);
        }
        // 当 initialLicenses 变化时，检查是否需要更新 selectedLicenses
        if (initialLicenses.length !== selectedLicenses.length ||
            !initialLicenses.every((license) => selectedLicenses.includes(license))) {
            // eslint-disable-next-line no-console
            console.log('initialLicenses changed, updating selectedLicenses');
            setSelectedLicenses(initialLicenses);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialLicenses]);

    // 从系统设置中获取州数据并排序
    const allStateOptions = sortByProperty(setting[EnumFields.STATE] || [], 'label').map((option) => ({
        id: option.value as string,
        label: option.label,
    }));

    // 根据搜索词过滤州选项
    const filteredStateOptions = allStateOptions.filter((state) =>
        state.label.toLowerCase().includes(searchTerm.toLowerCase()),
    );

    const handleLicenseChange = (stateId: string, checked: boolean) => {
        // eslint-disable-next-line no-console
        console.log('handleLicenseChange called:', { stateId, checked, currentSelected: selectedLicenses });

        let newSelectedLicenses: string[];

        if (checked) {
            newSelectedLicenses = [...selectedLicenses, stateId];
        } else {
            newSelectedLicenses = selectedLicenses.filter((id) => id !== stateId);
        }

        // eslint-disable-next-line no-console
        console.log('New selected licenses:', newSelectedLicenses);
        setSelectedLicenses(newSelectedLicenses);
    };

    const handleNext = () => {
        // 在点击Next时才提交数据
        if (onLicensesChange) {
            onLicensesChange(selectedLicenses);
        }

        if (onNext) {
            onNext();
        }
    };

    const handleReset = () => {
        // eslint-disable-next-line no-console
        console.log('handleReset called - clearing search term and selected licenses');
        setSearchTerm('');
        setSelectedLicenses([]);
    };

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        // eslint-disable-next-line no-console
        console.log('Search term changing from', searchTerm, 'to', e.target.value);
        // eslint-disable-next-line no-console
        console.log('Selected licenses before search:', selectedLicenses);
        setSearchTerm(e.target.value);

        // 添加一个setTimeout来检查状态变化
        setTimeout(() => {
            // eslint-disable-next-line no-console
            console.log('Selected licenses after search:', selectedLicenses);
        }, 0);
    };

    const removeSelectedItem = (item: string) => {
        setSelectedLicenses(selectedLicenses.filter((id) => id !== item));
    };

    const handleSave = () => {
        setSelectedLicenses(selectedLicenses);
        // 关闭下拉面板
        setIsDropdownOpen(false);
    };

    const dropdownRender = () => (
        <div
            className={cx(s.customDropdown, {
                [s.hasSearchResults]: searchTerm && filteredStateOptions.length > 0,
                [s.noSearchResults]: searchTerm && filteredStateOptions.length === 0,
                [s.hasSelectedItems]: selectedLicenses.length > 0,
                [s.isLoading]: loading,
            })}
        >
            <div className={s.searchContainer}>
                <div className={s.searchBox}>
                    <Input
                        type="text"
                        placeholder="Search..."
                        className={s.searchInput}
                        value={searchTerm}
                        onChange={handleSearchChange}
                        maxLength={20}
                    />
                </div>
            </div>
            {selectedLicenses.length > 0 && (
                <div className={s.selectedContainer}>
                    <div className={s.selectedWrap}>
                        <h4 className={s.selectedTitle}>Selected:</h4>
                        <div className={s.seletedContent}>
                            {selectedLicenses.map((item) => {
                                const selectedState = allStateOptions.find(
                                    (state) => state.id === item,
                                );
                                return (
                                    <div key={item} className={s.selectedItem}>
                                        {selectedState?.label || item}
                                        <div
                                            className={s.closeBtn}
                                            onClick={() =>
                                                removeSelectedItem(item)
                                            }
                                        >
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                width="11"
                                                height="11"
                                                viewBox="0 0 11 11"
                                                fill="none"
                                            >
                                                <path
                                                    d="M6.10761 5.49959L9.19524 1.81913C9.247 1.75796 9.20348 1.66504 9.12349 1.66504H8.18485C8.12957 1.66504 8.07664 1.68974 8.04017 1.73208L5.49361 4.76797L2.94704 1.73208C2.91176 1.68974 2.85883 1.66504 2.80237 1.66504H1.86372C1.78374 1.66504 1.74022 1.75796 1.79197 1.81913L4.87961 5.49959L1.79197 9.18005C1.78038 9.19369 1.77294 9.21036 1.77054 9.22809C1.76814 9.24583 1.77088 9.26388 1.77844 9.2801C1.78599 9.29633 1.79805 9.31004 1.81316 9.31962C1.82828 9.3292 1.84583 9.33424 1.86372 9.33414H2.80237C2.85765 9.33414 2.91058 9.30944 2.94704 9.26709L5.49361 6.23121L8.04017 9.26709C8.07546 9.30944 8.12839 9.33414 8.18485 9.33414H9.12349C9.20348 9.33414 9.247 9.24122 9.19524 9.18005L6.10761 5.49959Z"
                                                    fill="#0E62F4"
                                                />
                                            </svg>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="316"
                        height="2"
                        viewBox="0 0 316 2"
                        fill="none"
                    >
                        <path
                            d="M1 1H315.5"
                            stroke="#D6DADF"
                            strokeWidth="0.8"
                            strokeLinecap="round"
                            strokeDasharray="1.6 1.6"
                        />
                    </svg>
                </div>
            )}
            <div className={s.optionsList}>
                {filteredStateOptions.map((option) => (
                    <div
                        key={option.id}
                        className={`ant-select-item ant-select-item-option ${
                            selectedLicenses.includes(option.id)
                                ? 'ant-select-item-option-selected'
                                : ''
                        }`}
                        onClick={() => {
                            const isSelected = selectedLicenses.includes(
                                option.id,
                            );
                            // eslint-disable-next-line no-console
                            console.log('Custom dropdown option clicked:', {
                                optionId: option.id,
                                optionLabel: option.label,
                                isSelected,
                                currentSelected: selectedLicenses,
                            });

                            if (isSelected) {
                                const newSelected = selectedLicenses.filter(
                                    (id) => id !== option.id,
                                );
                                // eslint-disable-next-line no-console
                                console.log(
                                    'Removing selection, new state:',
                                    newSelected,
                                );
                                setSelectedLicenses(newSelected);
                            } else {
                                const newSelected = [
                                    ...selectedLicenses,
                                    option.id,
                                ];
                                // eslint-disable-next-line no-console
                                console.log(
                                    'Adding selection, new state:',
                                    newSelected,
                                );
                                setSelectedLicenses(newSelected);
                            }
                        }}
                    >
                        <div className="ant-select-item-option-content">
                            {option.label}
                        </div>
                        {selectedLicenses.includes(option.id) && (
                            <span className="ant-select-item-option-state">
                                ✓
                            </span>
                        )}
                    </div>
                ))}
            </div>
            <div className={s.dropdownFooter}>
                <button
                    type="button"
                    className={s.resetButton}
                    onClick={handleReset}
                >
                    Reset
                </button>
                <button
                    type="button"
                    className={s.saveButton}
                    onClick={handleSave}
                >
                    Save
                </button>
            </div>
        </div>
    );

    return (
        <>
            <OnboardingStepContainer
                currentStep={currentStep}
                totalSteps={totalSteps}
                title="Which states are you currently licensed in?"
                subtitle="Please select all that apply."
                showBackButton
                onBack={onBack}
            >
                <div className={cx(s.pc, s.statesContainer)}>
                    {filteredStateOptions.map((state) => (
                        <div key={state.id} className={s.stateWrapper}>
                            <CheckboxOption
                                id={state.id}
                                label={state.label}
                                checked={selectedLicenses.includes(state.id)}
                                onChange={handleLicenseChange}
                                size="compact"
                                width={{
                                    pc: 200,
                                    mobile: '100%',
                                }}
                            />
                        </div>
                    ))}
                </div>
                <div className={cx(s.mobile, s.statesMobileContainer)}>
                    <Select
                        mode="multiple"
                        placeholder="Select your states"
                        value={selectedLicenses}
                        open={isDropdownOpen}
                        onChange={(values: string[]) => {
                            // eslint-disable-next-line no-console
                            console.log(
                                'Mobile Select onChange called - ignoring to prevent conflicts:',
                                { values, previousSelected: selectedLicenses },
                            );
                            // 不执行任何操作，避免与自定义下拉面板的选择逻辑冲突
                            // setSelectedLicenses(values);
                        }}
                        options={allStateOptions.map((option) => ({
                            value: option.id,
                            label: option.label,
                        }))}
                        className={cx(s.statesSelect, {
                            [s.statesSelectOpen]: isDropdownOpen,
                            [s.statesSelectClosed]: !isDropdownOpen,
                        })}
                        popupClassName={s.customDropdownContainer}
                        maxTagCount={0}
                        maxTagPlaceholder={() =>
                            `${selectedLicenses.length} state${
                                selectedLicenses.length !== 1 ? 's' : ''
                            } selected`
                        }
                        disabled={loading}
                        showSearch={false}
                        dropdownStyle={{ zIndex: 9999 }}
                        dropdownRender={dropdownRender}
                        onDropdownVisibleChange={(visible) => {
                            setIsDropdownOpen(visible);
                        }}
                        // 阻止默认的选择行为，完全由自定义下拉面板控制
                        onSelect={() => false}
                        onDeselect={() => false}
                        // suffixIcon={<SearchIcon className={s.suffixIcon} />}
                    />

                    {selectedLicenses.length > 0 && (
                        <div
                            className={cx(s.selectedWrap, s.middleSelectedWrap)}
                        >
                            <h4 className={s.selectedTitle}>Selected states</h4>
                            <div className={s.seletedContent}>
                                {selectedLicenses.map((item) => {
                                    const selectedState = allStateOptions.find(
                                        (state) => state.id === item,
                                    );
                                    return (
                                        <div
                                            key={item}
                                            className={cx(
                                                s.selectedItem,
                                                s.middleSelectedItem,
                                            )}
                                        >
                                            {selectedState?.label || item}
                                            <div
                                                className={s.closeBtn}
                                                onClick={() =>
                                                    removeSelectedItem(item)
                                                }
                                            >
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    width="11"
                                                    height="11"
                                                    viewBox="0 0 11 11"
                                                    fill="none"
                                                >
                                                    <path
                                                        d="M6.10761 5.49959L9.19524 1.81913C9.247 1.75796 9.20348 1.66504 9.12349 1.66504H8.18485C8.12957 1.66504 8.07664 1.68974 8.04017 1.73208L5.49361 4.76797L2.94704 1.73208C2.91176 1.68974 2.85883 1.66504 2.80237 1.66504H1.86372C1.78374 1.66504 1.74022 1.75796 1.79197 1.81913L4.87961 5.49959L1.79197 9.18005C1.78038 9.19369 1.77294 9.21036 1.77054 9.22809C1.76814 9.24583 1.77088 9.26388 1.77844 9.2801C1.78599 9.29633 1.79805 9.31004 1.81316 9.31962C1.82828 9.3292 1.84583 9.33424 1.86372 9.33414H2.80237C2.85765 9.33414 2.91058 9.30944 2.94704 9.26709L5.49361 6.23121L8.04017 9.26709C8.07546 9.30944 8.12839 9.33414 8.18485 9.33414H9.12349C9.20348 9.33414 9.247 9.24122 9.19524 9.18005L6.10761 5.49959Z"
                                                        fill="#0E62F4"
                                                    />
                                                </svg>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>
            </OnboardingStepContainer>

            <ActionButtons
                showBack
                onNext={handleNext}
                nextDisabled={selectedLicenses.length === 0 || loading}
                currentStep={currentStep}
                onBack={onBack}
            />
        </>
    );
}
